import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import i18n from '@/i18n/config';

export interface AdminUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  company_id: string | null;
  is_active: boolean;
  deleted_at: string | null;
  created_at: string;
  company?: {
    id: string;
    name: string;
    type: string;
    created_at: string;
    founded_date: string | null;
  } | null;
  role?: string;
}

export interface AdminUsersFilters {
  role?: string;
  status?: 'active' | 'suspended' | 'deleted';
  company_id?: string;
  date_from?: string;
  date_to?: string;
  /** Deleted (anonymised) accounts are hidden unless this is true. */
  includeDeleted?: boolean;
}

export const useAdminUsers = (filters?: AdminUsersFilters) => {
  return useQuery({
    queryKey: ['admin-users', filters],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        .from('users')
        .select(`
          *,
          company:companies(id, name, type, created_at, founded_date)
        `)
        .order('created_at', { ascending: false });

      if (filters?.status === 'active') {
        query = query.eq('is_active', true).is('deleted_at', null);
      } else if (filters?.status === 'suspended') {
        query = query.eq('is_active', false).is('deleted_at', null);
      } else if (filters?.status === 'deleted') {
        query = query.not('deleted_at', 'is', null);
      } else if (!filters?.includeDeleted) {
        // Default view: hide anonymised accounts
        query = query.is('deleted_at', null);
      }

      if (filters?.company_id) {
        query = query.eq('company_id', filters.company_id);
      }

      if (filters?.date_from) {
        query = query.gte('created_at', filters.date_from);
      }

      if (filters?.date_to) {
        query = query.lte('created_at', filters.date_to);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Get user roles separately and merge
      const usersWithRoles = await Promise.all(
        (data || []).map(async (user) => {
          const { data: userRole } = await supabase
            .from('user_roles')
            .select('roles(code)')
            .eq('user_id', user.id)
            .limit(1)
            .single();

          return {
            ...user,
            role: userRole?.roles?.code || 'unknown',
          } as AdminUser;
        })
      );

      // Filter by role if specified
      let filteredData = usersWithRoles;
      if (filters?.role) {
        filteredData = usersWithRoles.filter(u => u.role === filters.role);
      }

      return filteredData;
    },
  });
};

export const useSuspendUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from('users')
        .update({ is_active: false })
        .eq('id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('User suspended');
    },
    onError: (error) => {
      console.error('Error suspending user:', error);
      toast.error('Error suspending user');
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.functions.invoke('admin-delete-account', {
        body: { user_id: userId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(i18n.t('admin.users.deleteSuccess'));
    },
    onError: (error: Error) => {
      console.error('Error deleting user:', error);
      toast.error(i18n.t('admin.users.deleteError') + ': ' + error.message);
    },
  });
};

export const usePromoteToAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      // First, get the admin role ID
      const { data: adminRole } = await supabase
        .from('roles')
        .select('id')
        .eq('code', 'admin')
        .single();

      if (!adminRole) throw new Error('Admin role not found');

      // Add admin role to user
      const { error } = await supabase
        .from('user_roles')
        .insert({
          user_id: userId,
          role_id: adminRole.id,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('User promoted to admin');
    },
    onError: (error) => {
      console.error('Error promoting user:', error);
      toast.error('Error promoting user');
    },
  });
};
